import React, { FC, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Trans, t } from '@lingui/macro'
import { getPromotedChannels } from 'api/schedule/getPromotedChannels'
import { getPromotedChannelsForRoot } from 'api/schedule/getPromotedChannelsForRoot'
import { channelSelector } from 'redux-store/channel/channel.selectors'
import { useQueryDataFetcher } from 'hooks/useQueryDataFetcher/useQueryDataFetcher'
import { useAppSelector } from 'redux-store/hooks'

import { Loader } from 'components/Loader/Loader'
import { ErrorMessage } from 'components/ErrorMessage/ErrorMessage'
import { ChannelList } from 'components/ChannelList/ChannelList'
import { ConditionalRender } from 'components/ConditionalRender/ConditionalRender'
import LoadMoreSection from 'components/LoadMoreSection/LoadMoreSection'

import styles from './TrendingChannels.module.scss'

interface Props {
  onTrendingChannelsExist: () => void
}
export const TrendingChannels: FC<Props> = ({ onTrendingChannelsExist }) => {
  const { company } = useParams<{ company: string | undefined }>()

  const channel = useAppSelector(channelSelector)

  const subdomain = channel?.data?.subdomain
  const isCurrentChannel = subdomain && subdomain === company
  const isRootChannel = isCurrentChannel && channel?.data?.isRootChannel
  const isChildOfRootChannel =
    isCurrentChannel && channel?.data?.isChildOfRootChannel
  const isRootOrChild = isRootChannel || isChildOfRootChannel

  const queryFunction = useCallback(() => {
    return isRootOrChild
      ? () => getPromotedChannelsForRoot(subdomain)
      : () => getPromotedChannels()
  }, [isRootOrChild])

  // The API for trendingChannels doesn't support pagination yet,
  // so we need to handle it manually for now.
  // We should refactor this using useInfiniteQueryDataFetcher
  // once the API supports pagination.
  const { isLoading, isError, data } = useQueryDataFetcher({
    queryKey: 'trendingChannels',
    queryFn: queryFunction()
  })

  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 6
  const totalItems = data ? data.length : 0
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE)

  const paginatedData = useMemo(() => {
    if (data) {
      return data.slice(0, currentPage * ITEMS_PER_PAGE)
    }
    return []
  }, [data, currentPage])

  if (isLoading) {
    return <Loader />
  }
  if (isError) {
    return (
      <ErrorMessage
        title={t`Something went wrong when trying to get the list of trending channels`}
        subText={''}
      />
    )
  }
  if (data && data.length > 0) {
    onTrendingChannelsExist()
    return (
      <>
        <div
          className={styles.Container}
          data-testid="trending-channels-container"
        >
          <h4>
            <Trans>Trending channels</Trans>
          </h4>
          <ConditionalRender mobileApp={false}>
            <ChannelList
              list={paginatedData}
              isOnRootOrChildChannel={isRootOrChild}
            />
          </ConditionalRender>
          <ConditionalRender web={false}>
            <ChannelList
              list={paginatedData}
              isOnRootOrChildChannel={isRootOrChild}
            />
          </ConditionalRender>
          <div>
            <LoadMoreSection
              onClick={() => setCurrentPage((prev) => prev + 1)}
              currentPage={currentPage}
              totalPages={totalPages}
            />
          </div>
        </div>
      </>
    )
  } else return null
}
